import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import NewsCard from '@/components/base/NewsCard';
import type { NewsArticle } from '@/types/news';
import { searchNews } from '@/utils/news';

const BASE_TIME = new Date('2026/10/12 12:00:00').getTime();
const DAY = 24 * 60 * 60 * 1000;

const timeOptions = [
  { key: 'all', label: '全部时间' },
  { key: 'week', label: '7天内' },
  { key: 'month', label: '30天内' },
  { key: 'year', label: '一年内' },
];

const sortOptions = [
  { key: 'latest', label: '按时间' },
  { key: 'hot', label: '按热度' },
];

const hotKeywords = ['轨道交通', '天气预警', '医保', '反诈', '电影节', '健康', '食品安全', '演唱会'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [timeRange, setTimeRange] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [visible, setVisible] = useState(6);

  useEffect(() => {
    setInput(query);
    setVisible(6);
  }, [query]);

  const results = useMemo(() => searchNews(query), [query]);

  const filtered = useMemo(() => {
    const list = results.filter((item: NewsArticle) => {
      const time = new Date(item.publishTime.replace(/-/g, '/')).getTime();
      if (timeRange === 'week') return time >= BASE_TIME - 7 * DAY;
      if (timeRange === 'month') return time >= BASE_TIME - 30 * DAY;
      if (timeRange === 'year') return time >= BASE_TIME - 365 * DAY;
      return true;
    });
    return list.sort((a, b) =>
      sortBy === 'hot' ? b.views - a.views : new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
    );
  }, [results, timeRange, sortBy]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchParams(input.trim() ? { q: input.trim() } : {});
  };

  const shown = filtered.slice(0, visible);

  return (
    <>
      <Seo
        title={query ? `搜索“${query}”-爆料网新闻爆料` : '站内搜索-爆料网新闻爆料'}
        description={`爆料网站内搜索结果，为你呈现与「${query || '新闻爆料'}」相关的权威报道与最新事件。`}
        keywords="爆料网搜索,新闻搜索,最新爆料,热点事件"
        path="/search"
      />

      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <nav aria-label="面包屑导航" className="flex items-center gap-1 text-sm text-foreground-500">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-primary-600">
            <i className="ri-home-4-line" />
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950">
            {query ? (
              <>
                搜索“<span className="text-primary-600">{query}</span>”的结果
              </>
            ) : (
              '站内搜索'
            )}
          </h1>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入关键词搜索新闻爆料..."
                aria-label="搜索新闻爆料"
                className="h-11 w-full rounded-md border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
              />
            </div>
            <button
              type="submit"
              className="inline-flex h-11 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
          <p className="mt-3 text-sm text-foreground-500">
            {query ? (
              <>
                共找到 <span className="font-medium text-secondary-600">{filtered.length}</span> 条相关爆料
              </>
            ) : (
              '输入关键词，快速定位你关心的事件与话题'
            )}
          </p>
        </div>

        {query ? (
          <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap text-sm text-foreground-500">时间</span>
              <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
                {timeOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTimeRange(item.key)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      timeRange === item.key ? 'bg-primary-600 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap text-sm text-foreground-500">排序</span>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setSortBy(item.key)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      sortBy === item.key ? 'bg-accent-500 text-primary-950' : 'text-foreground-600 hover:text-accent-700'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-4">
          {shown.length > 0 ? (
            shown.map((article) => <NewsCard key={article.id} article={article} variant="horizontal" />)
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-800">暂无搜索结果</h2>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或浏览下面的热门话题。</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {hotKeywords.map((keyword) => (
                  <Link
                    key={keyword}
                    to={`/search?q=${encodeURIComponent(keyword)}`}
                    className="rounded-md border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    #{keyword}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {shown.length > 0 && visible < filtered.length ? (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((prev) => prev + 6)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-arrow-down-line" />
              加载更多
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}